import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getStaffSizeLabel } from '../utils/inseeCodeMappings';
import { ClipboardIcon, MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';
import NotesButton from '../components/notes/NotesButton';
import api from '../api';

interface NAFInfo {
  code: string;
  intitule: string;
  intitule65?: string;
  intitule40?: string;
  niveau: string;
}

interface CompanyCategory {
  code: string;
  level: number;
  label: string;
}

interface Adherent {
  id: number;
  siret: string;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  description: string | null;
  createdAt: string;
  importFileId: number;
  importFile?: {
    id: number;
    originalFilename: string;
    createdAt: string;
  };
  bigData?: {
    // Note : not all fields are used, and not all fields are mapped from the big data API
    uniteLegale: {
      denominationUniteLegale: string;
      activitePrincipaleUniteLegale: string;
      categorieJuridiqueUniteLegale: string;
      trancheEffectifsUniteLegale: string;
      dateCreationUniteLegale: string;
      etatAdministratifUniteLegale: string;
      etablissementCount: number;
      nafInfo: NAFInfo | null;
      categorieJuridique: CompanyCategory | null;
      nomUniteLegale?: string;
      prenom1UniteLegale?: string;
    } | null;
    etablissement: {
      etablissementSiege: string;
      etatAdministratifEtablissement: string;
      libelleCommuneEtablissement: string;
      codePostalEtablissement: string;
      activitePrincipaleEtablissement: string;
      trancheEffectifsEtablissement: string;
      denominationUsuelleEtablissement: string;
      nafInfo: NAFInfo | null;
    } | null;
  };
}

const Adherents: React.FC = () => {
  const [adherents, setAdherents] = useState<Adherent[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortField, setSortField] = useState<keyof Adherent>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [showOnlyCesse, setShowOnlyCesse] = useState<boolean>(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const itemsPerPageOptions = [10, 25, 50];

  useEffect(() => {
    fetchAdherents();
    fetchUserInfo();
  }, []);

  // Fetch adherents when status filter changes
  useEffect(() => {
    fetchAdherents();
  }, [showOnlyCesse]);

  // Reset to first page when search term, items per page, or status filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage, showOnlyCesse]);

  const fetchUserInfo = async () => {
    try {
      const response = await api.get('/auth/me');
      setIsAdmin(response.data.user?.role === 'ROLE_ADMIN');
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
  };

  const fetchAdherents = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (showOnlyCesse) {
        params.append('statusFilter', 'cesse');
      }
      
      const response = await api.get(`/adherents?${params.toString()}`);
      setAdherents(response.data);
    } catch (error: any) {
      console.error('Error fetching adherents:', error);
      setError(
        error.response?.data?.message ||
          'Une erreur est survenue lors de la récupération des adhérents'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (field: keyof Adherent) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };
  
  // useEffect to scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const filteredAdherents = adherents.filter((adherent) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      adherent.siret.toLowerCase().includes(searchLower) ||
      (adherent.contactName && adherent.contactName.toLowerCase().includes(searchLower)) ||
      (adherent.contactEmail && adherent.contactEmail.toLowerCase().includes(searchLower)) ||
      (adherent.description && adherent.description.toLowerCase().includes(searchLower)) ||
      (adherent.bigData?.uniteLegale?.denominationUniteLegale && adherent.bigData.uniteLegale.denominationUniteLegale.toLowerCase().includes(searchLower)) ||
      (adherent.bigData?.etablissement?.denominationUsuelleEtablissement && adherent.bigData.etablissement.denominationUsuelleEtablissement.toLowerCase().includes(searchLower))
    );
  });

  const sortedAdherents = [...filteredAdherents].sort((a, b) => {
    const fieldA = a[sortField];
    const fieldB = b[sortField];

    if (fieldA === null && fieldB === null) return 0;
    if (fieldA === null) return sortDirection === 'asc' ? 1 : -1;
    if (fieldB === null) return sortDirection === 'asc' ? -1 : 1;
    if (fieldA === undefined && fieldB === undefined) return 0;
    if (fieldA === undefined) return sortDirection === 'asc' ? 1 : -1;
    if (fieldB === undefined) return sortDirection === 'asc' ? -1 : 1;

    if (typeof fieldA === 'string' && typeof fieldB === 'string') {
      return sortDirection === 'asc'
        ? fieldA.localeCompare(fieldB)
        : fieldB.localeCompare(fieldA);
    }

    // For dates or numbers
    return sortDirection === 'asc'
      ? (fieldA as any) > (fieldB as any) ? 1 : -1
      : (fieldA as any) < (fieldB as any) ? 1 : -1;
  });

  // Pagination calculations
  const totalItems = sortedAdherents.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedAdherents.slice(indexOfFirstItem, indexOfLastItem);

  // Generate page numbers to display
  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    
    // Always show first and last page
    // Show 2 pages before and after current page if possible
    const pageNumbers = [1];
    
    // Calculate start and end of the middle section
    let startPage = Math.max(2, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);
    
    // Adjust if we're near the start
    if (currentPage < 4) {
      endPage = 5;
    }
    
    // Adjust if we're near the end
    if (currentPage > totalPages - 3) {
      startPage = totalPages - 4;
    }
    
    // Add ellipsis if needed before middle section
    if (startPage > 2) {
      pageNumbers.push(-1); // -1 represents ellipsis
    }
    
    // Add middle pages
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    
    // Add ellipsis if needed after middle section
    if (endPage < totalPages - 1) {
      pageNumbers.push(-2); // -2 represents ellipsis to differentiate from the first one
    }
    
    // Add last page if not included yet
    pageNumbers.push(totalPages);
    
    return pageNumbers;
  };

  const pageNumbers = totalPages > 1 ? getPageNumbers() : [];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  };

  const renderSortIcon = (field: keyof Adherent) => {
    if (field !== sortField) {
      return <span className="text-gray-400">↕</span>;
    }
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        showSuccessToast('SIRET copié dans le presse-papier', 'siret-copied');
      })
      .catch((err) => {
        showErrorToast('Erreur lors de la copie du SIRET', 'siret-copy-error');
        console.error('Erreur lors de la copie :', err);
      });
  };

  const openGoogleSearch = (searchTerm: string) => {
    const encodedSearch = encodeURIComponent(searchTerm);
    window.open(`https://www.google.com/search?q=${encodedSearch}`, '_blank');
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Mes adhérents</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gérez la liste de vos adhérents et leurs informations de contact.
          </p>
        </div>
        <div className="flex space-x-3">
          <Link
            to="/adherents/create"
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            Ajouter un adhérent
          </Link>
          {isAdmin && (
            <Link
              to="/import-adherents"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Importer des adhérents
            </Link>
          )}
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-4">
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="w-full sm:w-1/2">
            <input
              type="text"
              placeholder="Rechercher un adhérent..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center">
              <label htmlFor="cesseSwitch" className="mr-2 text-sm font-medium text-gray-700 whitespace-nowrap">
                Cessés uniquement :
              </label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  id="cesseSwitch"
                  type="checkbox"
                  checked={showOnlyCesse}
                  onChange={(e) => setShowOnlyCesse(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
            <div className="flex items-center">
              <label htmlFor="itemsPerPage" className="mr-2 text-sm font-medium text-gray-700 whitespace-nowrap">
                Afficher :
              </label>
              <select
                id="itemsPerPage"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {itemsPerPageOptions.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Results summary */}
        {!isLoading && !error && adherents.length > 0 && (
          <div className="mb-4 px-4 py-2 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-600">
              {showOnlyCesse ? (
                <>
                  <span className="font-medium">{sortedAdherents.length}</span> adhérent{sortedAdherents.length > 1 ? 's' : ''} cessé{sortedAdherents.length > 1 ? 's' : ''} affiché{sortedAdherents.length > 1 ? 's' : ''}
                  {searchTerm && ` correspondant à "${searchTerm}"`}
                </>
              ) : (
                <>
                  <span className="font-medium">{sortedAdherents.length}</span> adhérent{sortedAdherents.length > 1 ? 's' : ''} au total
                  {searchTerm && ` correspondant à "${searchTerm}"`}
                </>
              )}
            </p>
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement des adhérents...</p>
          </div>
        ) : error ? (
          <div className="p-4 bg-red-100 text-red-700 rounded-md">
            {error}
          </div>
        ) : sortedAdherents.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600">
              {searchTerm && showOnlyCesse
                ? 'Aucun adhérent cessé ne correspond à votre recherche.'
                : searchTerm
                ? 'Aucun adhérent ne correspond à votre recherche.'
                : showOnlyCesse
                ? 'Aucun adhérent cessé trouvé.'
                : 'Aucun adhérent n\'a été ajouté pour le moment.'}
            </p>
            {!searchTerm && (
              <div className="mt-4 space-x-3">
                <Link
                  to="/adherents/create"
                  className="inline-block px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                >
                  Ajouter un adhérent
                </Link>
                {isAdmin && (
                  <Link
                    to="/import-adherents"
                    className="inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Importer des adhérents
                  </Link>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Adhérent
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('contactName')}
                  >
                    Contact {renderSortIcon('contactName')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('createdAt')}
                  >
                    Date d'ajout {renderSortIcon('createdAt')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentItems.map((adherent) => (
                  <tr key={adherent.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2 items-center">
                        <Link
                          to={`/adherents/${adherent.id}`}
                          className="text-blue-600 hover:text-blue-900 bg-blue-50 rounded-full px-2 py-0.5"
                        >
                          Voir
                        </Link>
                        <Link
                          to={`/adherents/${adherent.id}/edit`}
                          className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 rounded-full px-2 py-0.5"
                        >
                          Modifier
                        </Link>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-2">
                        <div className="font-medium text-gray-900">
                          {(adherent.bigData?.uniteLegale?.denominationUniteLegale && adherent.bigData?.uniteLegale?.denominationUniteLegale !== '[ND]') ? adherent.bigData.uniteLegale.denominationUniteLegale : 
                           (adherent.bigData?.uniteLegale?.nomUniteLegale && adherent.bigData?.uniteLegale?.prenom1UniteLegale && 
                            adherent.bigData.uniteLegale.nomUniteLegale !== '[ND]' && adherent.bigData.uniteLegale.prenom1UniteLegale !== '[ND]' ? 
                            `${adherent.bigData.uniteLegale.prenom1UniteLegale} ${adherent.bigData.uniteLegale.nomUniteLegale}` : 
                            (adherent.bigData?.etablissement?.denominationUsuelleEtablissement && adherent.bigData?.etablissement?.denominationUsuelleEtablissement !== '[ND]') ? 
                              adherent.bigData.etablissement.denominationUsuelleEtablissement : 
                              adherent.description || 
                              '-')}
                        </div>
                        <NotesButton
                          siret={adherent.siret}
                          companyName={
                            (adherent.bigData?.uniteLegale?.denominationUniteLegale && adherent.bigData?.uniteLegale?.denominationUniteLegale !== '[ND]') ? adherent.bigData.uniteLegale.denominationUniteLegale : 
                            (adherent.bigData?.uniteLegale?.nomUniteLegale && adherent.bigData?.uniteLegale?.prenom1UniteLegale && 
                             adherent.bigData.uniteLegale.nomUniteLegale !== '[ND]' && adherent.bigData.uniteLegale.prenom1UniteLegale !== '[ND]' ? 
                             `${adherent.bigData.uniteLegale.prenom1UniteLegale} ${adherent.bigData.uniteLegale.nomUniteLegale}` : 
                             (adherent.bigData?.etablissement?.denominationUsuelleEtablissement && adherent.bigData?.etablissement?.denominationUsuelleEtablissement !== '[ND]') ? 
                               adherent.bigData.etablissement.denominationUsuelleEtablissement : 
                               adherent.description || 
                               'Entreprise')
                          }
                          variant="icon"
                          size="sm"
                        />
                      </div>
                      <div className="flex items-center text-xs text-gray-600 mt-1">
                        <span>SIRET: {adherent.siret}</span>
                        <button 
                          onClick={() => copyToClipboard(adherent.siret)}
                          className="ml-2 p-1 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          title="Copier le SIRET"
                        >
                          <ClipboardIcon className="h-4 w-4 text-gray-500" />
                        </button>
                        <button 
                          onClick={() => {
                            const companyName = (adherent.bigData?.uniteLegale?.denominationUniteLegale && adherent.bigData?.uniteLegale?.denominationUniteLegale !== '[ND]') ? adherent.bigData.uniteLegale.denominationUniteLegale : 
                              (adherent.bigData?.uniteLegale?.nomUniteLegale && adherent.bigData?.uniteLegale?.prenom1UniteLegale && 
                               adherent.bigData.uniteLegale.nomUniteLegale !== '[ND]' && adherent.bigData.uniteLegale.prenom1UniteLegale !== '[ND]' ? 
                                `${adherent.bigData.uniteLegale.prenom1UniteLegale} ${adherent.bigData.uniteLegale.nomUniteLegale}` : 
                                (adherent.bigData?.etablissement?.denominationUsuelleEtablissement && adherent.bigData?.etablissement?.denominationUsuelleEtablissement !== '[ND]') ? 
                                adherent.bigData.etablissement.denominationUsuelleEtablissement : 
                                adherent.description || 
                                adherent.siret);
                            
                            const city = adherent.bigData?.etablissement?.libelleCommuneEtablissement && adherent.bigData?.etablissement?.libelleCommuneEtablissement !== '[ND]' ? 
                              adherent.bigData.etablissement.libelleCommuneEtablissement : '';
                            const searchQuery = city ? `${companyName} ${city}` : companyName;
                            
                            openGoogleSearch(searchQuery);
                          }}
                          className="ml-1 p-1 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          title="Rechercher sur Google"
                        >
                          <MagnifyingGlassIcon className="h-4 w-4 text-gray-500" />
                        </button>
                      </div>
                      {adherent.bigData?.uniteLegale?.categorieJuridique?.label && adherent.bigData.uniteLegale.categorieJuridique.label !== '[ND]' && (
                        <div className="text-xs text-gray-400 mt-1">
                          {adherent.bigData.uniteLegale.categorieJuridique.label}
                        </div>
                      )}
                      {adherent.bigData?.etablissement?.libelleCommuneEtablissement && adherent.bigData.etablissement.libelleCommuneEtablissement !== '[ND]' && (
                        <div className="text-xs text-gray-400 mt-1">
                          {adherent.bigData.etablissement.codePostalEtablissement !== '[ND]' ? adherent.bigData.etablissement.codePostalEtablissement : ''} {adherent.bigData.etablissement.libelleCommuneEtablissement}
                        </div>
                      )}
                      {adherent.bigData?.uniteLegale?.nafInfo && adherent.bigData.uniteLegale.nafInfo.intitule !== '[ND]' && (
                        <div className="text-xs text-gray-400 mt-1">
                          Activité: {adherent.bigData.uniteLegale.nafInfo.intitule}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <div>
                        {adherent.contactName && (
                          <div className="font-medium mb-1">{adherent.contactName}</div>
                        )}
                        {adherent.contactEmail && (
                          <div className="mb-1 text-xs">
                            <a
                              href={`mailto:${adherent.contactEmail}`}
                              className="text-blue-600 hover:underline"
                            >
                              {adherent.contactEmail}
                            </a>
                          </div>
                        )}
                        {adherent.contactPhone && (
                          <div className="text-xs">
                            <a
                              href={`tel:${adherent.contactPhone}`}
                              className="text-blue-600 hover:underline"
                            >
                              {adherent.contactPhone}
                            </a>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex flex-col">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          adherent.bigData?.etablissement?.etatAdministratifEtablissement === 'A' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {adherent.bigData?.etablissement?.etatAdministratifEtablissement === 'A' 
                            ? 'Actif' 
                            : 'Cessé'}
                        </span>
                        {adherent.bigData?.uniteLegale?.trancheEffectifsUniteLegale && adherent.bigData.uniteLegale.trancheEffectifsUniteLegale !== '[ND]' && (
                          <span className="text-xs mt-1 text-gray-500">
                            {getStaffSizeLabel(adherent.bigData.uniteLegale.trancheEffectifsUniteLegale)}
                          </span>
                        )}
                        {adherent.bigData?.uniteLegale && adherent.bigData.uniteLegale.etablissementCount > 0 && (
                          <span className="text-xs mt-1 text-gray-500 flex items-center">
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1">
                              {adherent.bigData.uniteLegale.etablissementCount}
                            </span>
                            établissement{adherent.bigData.uniteLegale.etablissementCount > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(adherent.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 sm:px-6 mt-4">
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center rounded-md px-4 py-2 text-sm font-medium ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Précédent
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {currentPage} sur {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative ml-3 inline-flex items-center rounded-md px-4 py-2 text-sm font-medium ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Suivant
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Affichage <span className="font-medium">{indexOfFirstItem + 1}</span> à{' '}
                      <span className="font-medium">
                        {Math.min(indexOfLastItem, totalItems)}
                      </span>{' '}
                      sur <span className="font-medium">{totalItems}</span> résultats
                    </p>
                  </div>
                  <div>
                    <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 ${
                          currentPage === 1
                            ? 'cursor-not-allowed'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <span className="sr-only">Précédent</span>
                        <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                      
                      {pageNumbers.map((pageNumber, index) => {
                        if (pageNumber < 0) {
                          // Render ellipsis
                          return (
                            <span
                              key={`ellipsis-${index}`}
                              className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300"
                            >
                              ...
                            </span>
                          );
                        }
                        
                        const isActive = pageNumber === currentPage;
                        
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => handlePageChange(pageNumber)}
                            disabled={isActive}
                            className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                              isActive
                                ? 'z-10 bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                                : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                            }`}
                            aria-current={isActive ? 'page' : undefined}
                          >
                            {pageNumber}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 ${
                          currentPage === totalPages
                            ? 'cursor-not-allowed'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <span className="sr-only">Suivant</span>
                        <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Adherents; 